import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { apiClient } from '@/lib/api-client';
import { storage } from '@/lib/storage';

const MAINTENANCE_BYPASS_KEY = 'gm_eng_mode_v1';
const MAINTENANCE_BYPASS_PARAM = 'gm_eng';

interface MaintenanceContextType {
  isMaintenanceMode: boolean;
  isLoading: boolean;
  isBypassed: boolean;
  checkMaintenanceStatus: () => Promise<void>;
  enableBypass: () => void;
  disableBypass: () => void;
}

const MaintenanceContext = createContext<MaintenanceContextType | undefined>(undefined);

export const useMaintenance = () => {
  const context = useContext(MaintenanceContext);
  if (context === undefined) {
    throw new Error('useMaintenance must be used within a MaintenanceProvider');
  }
  return context;
};

interface MaintenanceProviderProps {
  children: React.ReactNode;
}

export const MaintenanceProvider: React.FC<MaintenanceProviderProps> = ({ children }) => {
  const [isMaintenanceMode, setIsMaintenanceMode] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isBypassed, setIsBypassed] = useState(() => {
    if (typeof window === 'undefined') return false;
    
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get(MAINTENANCE_BYPASS_PARAM) === 'true') {
      localStorage.setItem(MAINTENANCE_BYPASS_KEY, 'true');
      return true;
    }
    
    return localStorage.getItem(MAINTENANCE_BYPASS_KEY) === 'true';
  });

  const enableBypass = useCallback(() => {
    localStorage.setItem(MAINTENANCE_BYPASS_KEY, 'true');
    setIsBypassed(true);
  }, []);

  const disableBypass = useCallback(() => {
    localStorage.removeItem(MAINTENANCE_BYPASS_KEY);
    setIsBypassed(false);
  }, []);

  const checkMaintenanceStatus = useCallback(async () => {
    try {
      const response = await apiClient.get<{
        success: boolean;
        maintenance: { enabled: boolean };
      }>('/site-config/public/maintenance');
      
      if (response?.success) {
        setIsMaintenanceMode(response.maintenance.enabled);
      }
    } catch {
      setIsMaintenanceMode(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initial check and polling fallback for unauthenticated users
  useEffect(() => {
    checkMaintenanceStatus();

    // Poll every 60 seconds as fallback (for unauthenticated users or if WebSocket fails)
    const intervalId = setInterval(checkMaintenanceStatus, 60000);

    return () => clearInterval(intervalId);
  }, [checkMaintenanceStatus]);

  // WebSocket subscription for real-time maintenance updates (authenticated users)
  useEffect(() => {
    const token = storage.getItem('auth_token') || storage.getItem('token');
    if (!token) return;

    let unsubscribe: (() => void) | null = null;

    import('@/lib/websocket-client').then(({ websocketClient }) => {
      websocketClient.connect(token).catch(() => {});
      
      unsubscribe = websocketClient.subscribe('maintenance:update', (data: { enabled: boolean; timestamp: string }) => {
        setIsMaintenanceMode(data.enabled);
      });
    });

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  const effectiveMaintenanceMode = useMemo(() => {
    if (isBypassed) {
      return false;
    }
    return isMaintenanceMode;
  }, [isMaintenanceMode, isBypassed]);

  return (
    <MaintenanceContext.Provider
      value={{
        isMaintenanceMode: effectiveMaintenanceMode,
        isLoading,
        isBypassed,
        checkMaintenanceStatus,
        enableBypass,
        disableBypass,
      }}
    >
      {children}
    </MaintenanceContext.Provider>
  );
};
